import { Component, DestroyRef, inject, ViewChild, input, output, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService, ModalDirective } from 'ngx-bootstrap/modal';
import { forkJoin } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { Equipe } from '../../bo/equipe';
import { Operation } from '../../bo/operation';
import { Projet } from '../../bo/projet';
import { ProprieteAffichee } from '../../bo/proprieteAffichee';
import { Rotation } from '../../bo/rotation';
import { FonctionsService } from '../../services/fonctions-partagees.service';
import { DonneesSuiviOperationnelModaleComponent } from '../donnees-suivi-operationnel-modale/donnees-suivi-operationnel-modale.component';
import { Equipment, EquipmentUtils } from 'src/app/bo/equipment';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { ShiftEnum, Shift } from 'src/app/bo/shifts/shift';
import { Operator } from 'src/app/bo/operator';
import { HibooService } from 'src/app/services/hiboo/hiboo.service';
import { EquipmentRouteService } from 'src/app/services/equipment-route.service';
import { EquipmentRouteDto } from 'src/app/bo/equipment/equipmentRoute';

@Component({
  selector: 'app-donnees-suivi-operationnel',
  templateUrl: './donnees-suivi-operationnel.component.html',
  styleUrls: ['./donnees-suivi-operationnel.component.scss'],
  standalone: false
})

export class DonneesSuiviOperationnelComponent {
  @ViewChild('modalExport') modalExport: ModalDirective;
  @ViewChild('modalConfirmSuppr') modalConfirmSuppr: ModalDirective;
  @ViewChild('modalConfirmUnassignRoute') modalConfirmUnassignRoute: ModalDirective;
  estProjet = input<boolean>();
  proprietesAfficheesDonneeSuivi = input<ProprieteAffichee[]>();
  tabDonneesSuivi = input<Rotation[]>();
  donneesChargees = input<boolean>();
  projet = input<Projet>();
  tirId = input<number>();
  rechargerDonnees = output<boolean>();
  modalModif: BsModalRef;
  siteId: number;
  projetId: number;
  aucuneDonnee = signal(false);
  tabEquipments = signal<Equipment[]>([]);
  tabShifts = signal<Shift[]>([]);
  tabEquipes = signal<Equipe[]>([]);
  tabOperateurs = signal<Operator[]>([]);
  tabRoutes = signal<EquipmentRouteDto[]>([]);
  isLoaded = signal(false);
  shiftIdToDelete = signal<number>(null);
  routeIdToUnassign = signal<number | null>(null);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly modalService: BsModalService,
    private readonly http: HttpService,
    private readonly route: ActivatedRoute,
    private readonly fonctionsService: FonctionsService,
    private readonly translate: TranslateService,
    private readonly utilService: UtilisateurService,
    private readonly hibooService: HibooService,
    private readonly equipmentRouteService: EquipmentRouteService,
  ) { }

  ngOnInit() {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.siteId = params.id;
      this.projetId = params.projectId;
    });
    this.refreshEquipmentsData();
  }

  ngOnChanges() {
    this.refreshEquipmentsData();
  }

  ouvrirModale(shift: Shift) {
    var initialState;

    if (shift) {
      const shiftCopy = { ...shift, date: new Date(shift.date) };

      initialState = {
        projet: this.projet(),
        tabTirs: this.projet().tirs,
        tabEquipes: this.tabEquipes(),
        tabOperateurs: this.tabOperateurs(),
        tabEquipments: this.tabEquipments(),
        estModif: true,
        siteId: this.siteId,
        dateShift: this.fonctionsService.normalisationDate(shiftCopy.date),
        tirSelectionne: this.tirId() && this.tirId() > 0 ? this.projet().tirs.find(o => o.tirId == this.tirId()) : null,
        propAffOperation: new Operation().getProprietesAffichees(),
        equipeSelectionnee: this.tabEquipes().find(x => x.equipeId == shiftCopy.equipeId),
        operateurSelectionne: this.tabOperateurs().find(x => x.operatorId == shiftCopy.operatorId),
        estProjet: this.estProjet(),
        shiftSelectione: shiftCopy,
        oldShiftSelectionne: shiftCopy,
        equipmentsSelectione: shiftCopy.equipements,
        oldEquipmentsSelectione: shiftCopy.equipements,
        timeSelectione: shiftCopy.time,
        tabShifts: this.tabShiftsSorted()
      };
    } else {
      initialState = {
        projet: this.projet(),
        tabTirs: this.projet().tirs,
        tabEquipes: this.tabEquipes(),
        tabOperateurs: this.tabOperateurs(),
        tabEquipments: this.tabEquipments(),
        estModif: false,
        siteId: this.siteId,
        tirSelectionne: this.tirId() && this.tirId() > 0 ? this.projet().tirs.find(o => o.tirId == this.tirId()) : null,
        estProjet: this.estProjet(),
        propAffOperation: new Operation().getProprietesAffichees(),
        tabShifts: this.tabShiftsSorted()
      };
    }
    this.fonctionsService.loading_screen(false);
    this.modalModif = this.modalService.show(DonneesSuiviOperationnelModaleComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, class: 'modal-lg', initialState });

    this.modalModif.content.deletedIdEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (deletedId: number) => {
        this.tabShifts.update(shifts => shifts.filter(x => x.shiftId !== deletedId));
      }
    });

    this.modalModif.content.addDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: Shift) => {
        data.equipe = this.tabEquipes().find(e => e.equipeId === data.equipeId);
        data.operator = this.tabOperateurs().find(e => e.operatorId === data.operatorId);
        this.tabShifts.update(shifts => [...shifts, data]);
        this.hibooService.updateCacheShifts(this.tirId(), this.tabShifts());
      }
    });

    this.modalModif.content.modifDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: Shift) => {
        const index = this.tabShifts().findIndex(x => x.shiftId === data.shiftId);
        if (index > -1) {
          const equipementExternalIds = data.equipementsIds
            .split(';')
            .map(id => parseInt(id.trim(), 10));
          data.equipements = this.tabEquipments().filter(e =>
            equipementExternalIds.includes(e.externalId)
          );
          data.equipe = this.tabEquipes().find(e => e.equipeId === data.equipeId);
          data.operator = this.tabOperateurs().find(e => e.operatorId === data.operatorId);
          this.tabShifts.update(shifts => {
            const updated = [...shifts];
            updated.splice(index, 1, data);
            return updated;
          });
          this.hibooService.updateCacheShifts(this.tirId(), this.tabShifts());
        }
      }
    });

    var modalSubscription = this.modalService.onHide.subscribe(() => {
      modalSubscription.unsubscribe();
    });
  }

  lancerExport() {
    this.fonctionsService.loading_screen(true);
    this.aucuneDonnee.set(false);
    this.http.get<boolean>('Projects/' + this.projet().projetId + '/GotAnalysisData').subscribe(resultat => {
      this.fonctionsService.loading_screen(false);
      if (resultat)
        this.exportDonneeSuivi();
      else {
        this.aucuneDonnee.set(true);
        this.modalExport.show();
      }
    }, () => {
      this.fonctionsService.loading_screen(false);
    });
  }

  private refreshEquipmentsData() {
    this.fonctionsService.loading_screen(true);

    this.tabEquipes.set([]);
    this.tabEquipments.set([]);
    this.tabOperateurs.set([]);

    var equipes = this.http.get<Equipe[]>('Sites/' + this.utilService.currentSite().siteId + '/Teams');
    var equipments = this.http.get<Equipment[]>('Hiboo/' + this.utilService.currentSite().siteId + '/GetMobileEquipments');
    var operateurs = this.http.get<Operator[]>('Sites/' + this.utilService.currentSite().siteId + '/Operators');

    forkJoin([equipes, equipments, operateurs]).subscribe({
      next: (resultats) => {

        this.tabEquipes.set(resultats[0]);
        this.tabOperateurs.set(resultats[2]);

        if(resultats[1] === null) return;
        let equipment = resultats[1].map(obj => ({ ...obj, withExternalConnection: false }));
        equipment = equipment.filter(item =>
          !this.tabEquipments().some(existingItem => existingItem.externalId === item.externalId)
        );
        this.tabEquipments.update(current => [...current, ...equipment]);
        this.tabEquipments.update(current => current.filter(e => e.externalId));
        this.tabEquipments().forEach(element => {
          element.displayName = EquipmentUtils.formatEquipmentDisplayName(element);
        });

        this.refreshShiftsData();
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      }
    })
  }

  private refreshShiftsData() {
    this.hibooService.getShiftsByTirId(this.utilService.currentTir.siteId, this.tirId()).subscribe({
      next: (shifts) => {
        this.tabShifts.set([]);
        this.tabShifts.update(current => [...current, ...shifts]);

        this.tabShifts().forEach((shift) => {
          const equipmentIds: number[] = shift.equipementsIds.split(';').map(Number);
          shift.equipements = this.tabEquipments().filter(equipment => equipmentIds.includes(equipment.externalId));
          shift.equipe = this.tabEquipes().find(team => team.equipeId === shift.equipeId);
          shift.operator = this.tabOperateurs().find(operator => operator.operatorId === shift.operatorId);
        });

        // Charger les routes associées au tir
        this.refreshRoutesData();
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      }
    });
  }

  private refreshRoutesData() {
    this.equipmentRouteService.getRoutesByTirId(this.tirId()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (routes) => {
        this.tabRoutes.set(routes);
        this.isLoaded.set(true);
        this.fonctionsService.loading_screen(false);
      },
      error: () => {
        // En cas d'erreur, continuer sans routes
        this.tabRoutes.set([]);
        this.isLoaded.set(true);
        this.fonctionsService.loading_screen(false);
      }
    });
  }

  getEquipmentName(equipmentId: number): string {
    const equipment = this.tabEquipments().find(e => e.externalId === equipmentId);
    return equipment ? equipment.displayName : `Equipment ${equipmentId}`;
  }

  formatRouteDate(date: Date | string): string {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  }

  formatRouteTime(date: Date | string): string {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }

  getDurationMinutes(start: Date | string, end: Date | string): number {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    return Math.round(diffMs / (1000 * 60));
  }

  get tabShiftsSorted() {
    return computed(() => 
      this.tabShifts()
        .filter(shift => !shift.isDeleted)
        .filter((shift, index, self) =>
          index === self.findIndex(s =>
            s.equipementsIds === shift.equipementsIds &&
            s.time === shift.time &&
            new Date(s.date).getDate() === new Date(shift.date).getDate()
          )
        )
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA > dateB) return -1;
          if (dateA < dateB) return 1;

          return b.time - a.time;
        })
    );
  }

  exportDonneeSuivi() {
    var startLoader = setTimeout(() => {
      this.modalExport.show();
    }, 300);

    this.http.downloadFile('Projects/' + this.projet().projetId + '/ExportRotations').subscribe((resultat: ArrayBuffer) => {
      const blob = new Blob([resultat], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = this.projet().nom + ".csv";
      document.body.appendChild(a);
      a.click();
      this.modalExport.hide();
      clearTimeout(startLoader);
      setTimeout(() => {
        a.remove();
      }, 100);
    }, () => {
      this.modalExport.hide();
      clearTimeout(startLoader);
    });
  }

  protected deleteShift() {
    this.http.delete<number>('Blasts/' + this.utilService.currentSite().siteId + '/shifts/' + this.shiftIdToDelete()).subscribe({
      next: (response) => {
        this.fonctionsService.launch_toast(true);
        this.tabShifts.update(shifts => shifts.filter(x => x.shiftId != this.shiftIdToDelete()));
        this.hibooService.updateCacheShifts(this.tirId(), this.tabShifts());
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
      }
    })
    this.hideConfirmSuppr();
  }

  protected showConfirmSuppr(shiftIdToDelete: number) {
    this.shiftIdToDelete.set(shiftIdToDelete);
    this.modalConfirmSuppr.show();
  }

  protected hideConfirmSuppr() {
    this.modalConfirmSuppr.hide();
  }

  showConfirmUnassignRoute(routeId: number) {
    this.routeIdToUnassign.set(routeId);
    this.modalConfirmUnassignRoute.show();
  }

  hideConfirmUnassignRoute() {
    this.modalConfirmUnassignRoute.hide();
  }

  unassignRoute() {
    if (this.routeIdToUnassign() == null) return;
    const routeId = this.routeIdToUnassign();

    this.equipmentRouteService.deleteRoute(routeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.tabRoutes.update(routes => routes.filter(r => r.id !== routeId));
        this.routeIdToUnassign.set(null);
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
        this.routeIdToUnassign.set(null);
      }
    });
    this.hideConfirmUnassignRoute();
  }

  protected enumToString(enumValue: any) {
    switch (enumValue) {
      case ShiftEnum.NightToMorning:
        return 'DONNEE_SUIVI_OP.MidnightToMorning';
      case ShiftEnum.MorningToAfternoon:
        return 'DONNEE_SUIVI_OP.MorningToAfternoon';
      case ShiftEnum.AfternoonToEvening:
        return 'DONNEE_SUIVI_OP.AfternoonToMidnight';
    }
  }

  trackByShiftId(index: number, shift: Shift): number {
    return shift.shiftId; // or any unique property
  }
}
